import React, { useMemo, useState } from "react";
import { FormSection, FormSections } from "../ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Chips } from "primereact/chips";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import toast from "react-hot-toast";
import {
  useCreateSupportArticleMutation,
  useGetSupportCategoriesQuery,
  useUpdateSupportArticleMutation,
} from "../../store/services/support";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  CreateSupportArticle,
  SupportArticleVersion,
  UpdateSupportArticle,
} from "../../models/support";
import { formatSupportDateTime } from "../../utils/functions/parseInstant";

const getApiError = (e: any): string => e?.data?.error?.message || "Ocurrió un error";

export interface SupportArticleFormValues {
  title: string;
  category_id: string;
  body: string;
  search_tags: string[];
  sort_order: number | null;
  change_summary: string;
  is_active: boolean;
  tenant_id: string;
}

interface SupportArticleFormProps {
  initialValues?: Partial<SupportArticleFormValues>;
  isEdit: boolean;
  articleId?: string;
  /** Tenant al que ya pertenece el artículo (solo relevante en edición). */
  articleTenantId?: string | null;
  /** Nombre de la categoría actual (para conservarla en el dropdown si quedó inactiva). */
  initialCategoryName?: string | null;
  versions?: SupportArticleVersion[];
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .required("El título es requerido")
    .max(200, "Máximo 200 caracteres"),
  category_id: Yup.string().required("La categoría es requerida"),
  body: Yup.string().trim().required("El contenido es requerido"),
  search_tags: Yup.array().of(Yup.string()).max(20, "Máximo 20 tags"),
  sort_order: Yup.number().nullable().notRequired(),
  change_summary: Yup.string().notRequired(),
});

const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const renderInline = (s: string): string =>
  s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");

// Render manual de markdown SOLO para la vista previa (títulos, listas, bold, italic).
export const renderMarkdownPreview = (md: string): string => {
  const lines = md.split("\n");
  let html = "";
  let inList = false;

  lines.forEach((rawLine) => {
    const line = escapeHtml(rawLine);
    const listMatch = line.match(/^\s*-\s+(.*)$/);
    if (listMatch) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      html += `<li>${renderInline(listMatch[1])}</li>`;
      return;
    }
    if (inList) {
      html += "</ul>";
      inList = false;
    }
    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      html += `<h${level}>${renderInline(headingMatch[2])}</h${level}>`;
      return;
    }
    if (line.trim() === "") return;
    html += `<p>${renderInline(line)}</p>`;
  });

  if (inList) html += "</ul>";
  return html;
};

export function SupportArticleForm({
  initialValues,
  isEdit,
  articleId,
  articleTenantId,
  initialCategoryName,
  versions,
}: SupportArticleFormProps) {
  const NO_TENANT_LABEL = 'Sin tenant'
  const NO_TENANT_VALUE = '__NO_TENANT__'
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const { data: categories, isLoading: isLoadingCategories } = useGetSupportCategoriesQuery({
    isActive: true,
  });
  const [createArticle, { isLoading: isCreating }] = useCreateSupportArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateSupportArticleMutation();
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();

  const isSaving = isCreating || isUpdating;

  const tenantOptions = useMemo(
    () => [
      { id: NO_TENANT_VALUE, displayName: NO_TENANT_LABEL },
      ...(Array.isArray(tenants)
        ? tenants.map((tenant) => ({
          ...tenant,
          displayName: `${tenant.provider || tenant.id} ${tenant.emailDomain ? `(${tenant.emailDomain})` : ""
            }`.trim(),
        }))
        : []),
    ],
    [tenants]
  );

  const formik = useFormik<SupportArticleFormValues>({
    initialValues: {
      title: initialValues?.title ?? "",
      category_id: initialValues?.category_id ?? "",
      body: initialValues?.body ?? "",
      search_tags: initialValues?.search_tags ?? [],
      sort_order: initialValues?.sort_order ?? null,
      change_summary: initialValues?.change_summary ?? "",
      is_active: initialValues?.is_active ?? true,
      tenant_id: initialValues?.tenant_id ?? NO_TENANT_VALUE,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (isEdit && articleId) {
        const updateData: UpdateSupportArticle = {
          id: articleId,
          title: values.title,
          body: values.body,
          category_id: values.category_id,
          search_tags: values.search_tags,
          is_active: values.is_active,
          ...(values.sort_order !== null && { sort_order: values.sort_order }),
          ...(values.change_summary.trim() && { change_summary: values.change_summary.trim() }),
          // El tenant de un artículo existente no se reasigna desde acá: se reenvía el
          // que ya tenía, únicamente para que el back-office resuelva el scope de un SUPER_ADMIN.
          ...(!tenantScope.hidden && { tenant_id: articleTenantId ?? null }),
        };
        updateArticle(updateData)
          .unwrap()
          .then(() => {
            toast.success("Artículo actualizado exitosamente!");
            navigate("/soporte/articulos");
          })
          .catch((error) => {
            toast.error(getApiError(error));
          });
      } else {
        const createData: CreateSupportArticle = {
          title: values.title,
          body: values.body,
          category_id: values.category_id,
          search_tags: values.search_tags,
          ...(values.sort_order !== null && { sort_order: values.sort_order }),
          tenant_id: values.tenant_id === NO_TENANT_VALUE ? null : values.tenant_id || null,
        };
        createArticle(createData)
          .unwrap()
          .then(() => {
            toast.success("Artículo creado exitosamente!");
            navigate("/soporte/articulos");
          })
          .catch((error) => {
            toast.error(getApiError(error));
          });
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } =
    formik;

  React.useEffect(() => {
    if (isEdit) return;
    if (tenantScope.locked && tenantScope.tenantId && values.tenant_id !== tenantScope.tenantId) {
      setFieldValue("tenant_id", tenantScope.tenantId);
    }
  }, [isEdit, tenantScope.locked, tenantScope.tenantId, values.tenant_id, setFieldValue]);

  const categoryTenantId = isEdit
    ? articleTenantId ?? null
    : values.tenant_id === NO_TENANT_VALUE
      ? null
      : values.tenant_id;

  // Con scope global el endpoint trae categorías de todos los tenants y el back rechaza
  // una categoría que no sea del tenant del artículo: hay que acotar el dropdown.
  const mustScopeCategoriesByTenant = !tenantScope.locked;

  const categoryOptions = useMemo(() => {
    const options = (categories ?? [])
      .filter((c) => c.isActive)
      .filter((c) => !mustScopeCategoriesByTenant || c.tenantId === categoryTenantId)
      .map((c) => ({
        label: c.name,
        value: c.id,
      }));
    const currentCategoryId = initialValues?.category_id;
    if (
      isEdit &&
      currentCategoryId &&
      !options.some((option) => option.value === currentCategoryId)
    ) {
      const currentCategoryName = initialCategoryName?.trim();
      options.push({
        label: currentCategoryName
          ? `${currentCategoryName} (inactiva)`
          : "Categoría actual (inactiva)",
        value: currentCategoryId,
      });
    }
    return options;
  }, [
    categories,
    isEdit,
    initialValues?.category_id,
    initialCategoryName,
    mustScopeCategoriesByTenant,
    categoryTenantId,
  ]);

  React.useEffect(() => {
    if (isEdit || isLoadingCategories || !values.category_id) return;
    if (categoryOptions.some((option) => option.value === values.category_id)) return;
    setFieldValue("category_id", "");
  }, [isEdit, isLoadingCategories, values.category_id, categoryOptions, setFieldValue]);

  const previewHtml = useMemo(() => {
    if (!values.body) return "";
    return values.body.trimStart().startsWith("<")
      ? values.body
      : renderMarkdownPreview(values.body);
  }, [values.body]);

  const sortedVersions = useMemo(
    () => [...(versions ?? [])].sort((a, b) => b.version - a.version),
    [versions]
  );

  return (
    <div className="flex flex-column gap-4">
      <form onSubmit={handleSubmit} className="flex flex-column gap-4">
        <FormSections>
        <FormSection icon="pi-info-circle" title="Información general">
          <div className="formgrid grid">
            <div className="field col-12 md:col-6">
              <label htmlFor="title" className="font-medium text-900">
                Título *
              </label>
              <InputText
                id="title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSaving}
                maxLength={200}
                className={`w-full mt-2 ${touched.title && errors.title ? "p-invalid" : ""}`}
                placeholder="Título del artículo"
              />
              {touched.title && errors.title && (
                <small className="p-error block mt-1">{errors.title}</small>
              )}
            </div>

            <div className="field col-12 md:col-6">
              <label htmlFor="category_id" className="font-medium text-900">
                Categoría *
              </label>
              <Dropdown
                id="category_id"
                name="category_id"
                value={values.category_id || null}
                options={categoryOptions}
                optionLabel="label"
                optionValue="value"
                loading={isLoadingCategories}
                onChange={(e) => setFieldValue("category_id", e.value ?? "")}
                onBlur={handleBlur}
                disabled={isSaving}
                className={`w-full mt-2 ${touched.category_id && errors.category_id ? "p-invalid" : ""
                  }`}
                placeholder="Seleccioná una categoría"
              />
              {touched.category_id && errors.category_id && (
                <small className="p-error block mt-1">{errors.category_id}</small>
              )}
              {!isLoadingCategories && categoryOptions.length === 0 && (
                <small className="text-orange-600 block mt-1">
                  {mustScopeCategoriesByTenant && !categoryTenantId
                    ? "Elegí un tenant para ver sus categorías."
                    : "No hay categorías activas. Activá o creá una en la sección Categorías."}
                </small>
              )}
            </div>

            {!isEdit && !tenantScope.hidden && (
              <div className="field col-12 md:col-6">
                <label htmlFor="tenant_id" className="font-medium text-900">
                  Tenant
                </label>
                <Dropdown
                  id="tenant_id"
                  name="tenant_id"
                  value={values.tenant_id}
                  onChange={(e) => setFieldValue("tenant_id", e.value ?? NO_TENANT_VALUE)}
                  onBlur={handleBlur}
                  optionLabel="displayName"
                  optionValue="id"
                  options={tenantOptions}
                  loading={isLoadingTenants}
                  disabled={isSaving || tenantScope.locked}
                  className="w-full mt-2"
                  placeholder="Seleccione un tenant (opcional)"
                />
                {tenantScope.locked && (
                  <small className="text-600 block mt-1">
                    Fijado por el tenant seleccionado en la barra superior.
                  </small>
                )}
              </div>
            )}

            <div className="field col-12 md:col-6">
              <label htmlFor="sort_order" className="font-medium text-900">
                Orden
              </label>
              <InputNumber
                id="sort_order"
                name="sort_order"
                value={values.sort_order}
                onValueChange={(e) => setFieldValue("sort_order", e.value ?? null)}
                onBlur={handleBlur}
                disabled={isSaving}
                className="mt-2"
                inputStyle={{ width: "7rem" }}
                placeholder="Opcional"
                min={0}
              />
            </div>
          </div>
        </FormSection>

        <FormSection
          icon="pi-align-left"
          title="Contenido (Markdown o HTML) *"
          htmlFor="body"
          actions={
            <Button
              type="button"
              label={showPreview ? "Ocultar vista previa" : "Vista previa"}
              icon={showPreview ? "pi pi-eye-slash" : "pi pi-eye"}
              severity="secondary"
              outlined
              size="small"
              onClick={() => setShowPreview((prev) => !prev)}
            />
          }
        >
          <div className="formgrid grid">
            <div className="field col-12 mb-0">
              <InputTextarea
                id="body"
                name="body"
                value={values.body}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSaving}
                rows={12}
                autoResize
                className={`w-full ${touched.body && errors.body ? "p-invalid" : ""}`}
                placeholder="Escribí el contenido del artículo..."
              />
              {touched.body && errors.body && (
                <small className="p-error block mt-1">{errors.body}</small>
              )}
            </div>

            {showPreview && (
              <div className="field col-12 mt-3 mb-0">
                <label className="font-medium text-900">Vista previa</label>
                <div
                  className="mt-2 p-3 border-1 surface-border border-round surface-card"
                  style={{ minHeight: "120px", overflowX: "auto", wordBreak: "break-word" }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            )}
          </div>
        </FormSection>

        <FormSection icon="pi-tags" title="Tags de búsqueda" htmlFor="search_tags">
          <div className="formgrid grid">
            <div className="field col-12 mb-0">
              <Chips
                id="search_tags"
                name="search_tags"
                value={values.search_tags}
                max={20}
                onChange={(e) => setFieldValue("search_tags", e.value ?? [])}
                onBlur={handleBlur}
                disabled={isSaving}
                className={`w-full mt-2 ${touched.search_tags && errors.search_tags ? "p-invalid" : ""
                  }`}
                placeholder="Escribí un tag y presioná Enter"
              />
              {touched.search_tags && errors.search_tags && (
                <small className="p-error block mt-1">{errors.search_tags as string}</small>
              )}
              <small className="text-600 block mt-1">Máximo 20 tags</small>
            </div>
          </div>
        </FormSection>

        {isEdit && (
          <FormSection icon="pi-check-circle" title="Publicación">
            <div className="formgrid grid">
              <div className="field col-12 md:col-6 mb-0">
                <label htmlFor="change_summary" className="font-medium text-900">
                  Resumen del cambio
                </label>
                <InputText
                  id="change_summary"
                  name="change_summary"
                  value={values.change_summary}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                  className="w-full mt-2"
                  placeholder="Ej: se corrigió el paso 3"
                />
              </div>

              <div className="field col-12 md:col-6 mb-0">
                <label htmlFor="is_active" className="font-medium text-900 block">
                  Activo
                </label>
                <InputSwitch
                  id="is_active"
                  name="is_active"
                  checked={values.is_active}
                  onChange={(e) => setFieldValue("is_active", e.value)}
                  disabled={isSaving}
                  className="mt-2"
                />
              </div>
            </div>
          </FormSection>
        )}
        </FormSections>

        <div className="flex flex-wrap justify-content-center gap-4 mt-2">
          <Button
            icon="pi pi-check"
            severity="success"
            type="submit"
            label={isEdit ? "Actualizar artículo" : "Crear artículo"}
            loading={isSaving}
          />
          <Button
            icon="pi pi-times"
            severity="secondary"
            type="button"
            label="Cancelar"
            onClick={() => navigate("/soporte/articulos")}
            disabled={isSaving}
          />
        </div>
      </form>

      {isEdit && sortedVersions.length > 0 && (
        <FormSection icon="pi-history" title="Historial de versiones">
          <Accordion>
            {sortedVersions.map((version) => (
              <AccordionTab
                key={version.id}
                header={
                  <div
                    className="flex flex-wrap align-items-center gap-2 w-full"
                    style={{ minWidth: 0 }}
                  >
                    <span className="font-bold">v{version.version}</span>
                    <span className="text-600 text-sm">{version.changedByName || "—"}</span>
                    <span
                      className="text-600 text-sm"
                      style={{ minWidth: 0, wordBreak: "break-word" }}
                    >
                      {version.changeSummary || "Sin resumen"}
                    </span>
                    <span className="text-500 text-sm md:ml-auto white-space-nowrap">
                      {formatSupportDateTime(version.createdAt)}
                    </span>
                  </div>
                }
              >
                <p className="font-bold text-900 mt-0 mb-2">{version.title}</p>
                <div
                  className="text-700 m-0"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {version.body}
                </div>
              </AccordionTab>
            ))}
          </Accordion>
        </FormSection>
      )}
    </div>
  );
}
